import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Minus, XCircle } from 'lucide-react';
import { DASH, authHeader } from '../api';
import { SectionCard, Spinner, EmptyState, Badge, GhostButton, DangerButton, fmtDate } from '../ui';
import ConfirmDialog from '../../components/ConfirmDialog';

const MembershipsSection = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${DASH}/memberships`, { headers: authHeader() })
      .then((res) => setRows(res.data || []))
      .catch(() => setError('Could not load memberships.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const adjust = async (id, delta) => {
    try {
      await axios.post(`${DASH}/memberships/${id}/adjust-sessions`, { delta, reason: 'admin adjustment' }, { headers: authHeader() });
      load();
    } catch {
      setError('Could not adjust sessions.');
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setBusy(true);
    try {
      await axios.post(`${DASH}/memberships/${cancelTarget}/cancel`, {}, { headers: authHeader() });
      setCancelTarget(null);
      load();
    } catch {
      setError('Could not cancel membership.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="Loading memberships…" />;

  return (
    <div data-testid="wellness-admin-section-memberships" className="space-y-4">
      <div>
        <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>
          Memberships
        </h2>
        <p className="text-sm text-[#5a7062] mt-1">Active Wellness Membership subscribers.</p>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <SectionCard testId="memberships-card">
        {rows.length === 0 ? (
          <EmptyState testId="memberships-empty" label="No active memberships yet." />
        ) : (
          <div className="space-y-3">
            {rows.map((m) => (
              <div key={m.id} data-testid={`membership-row-${m.id}`} className="flex items-center justify-between gap-4 border border-[#f0ebe0] rounded-xl p-4">
                <div>
                  <div className="font-semibold text-[#0d1f15]">{m.first_name} <span className="text-xs text-[#5a7062] font-normal ml-1">{m.email}</span></div>
                  <div className="text-xs text-[#5a7062] mt-0.5">Next billing: {fmtDate(m.next_billing_date)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge tone="gold">{m.sessions_remaining} left / {m.total_sessions}</Badge>
                  <div className="flex items-center gap-1">
                    <GhostButton testId={`membership-dec-${m.id}`} onClick={() => adjust(m.id, -1)}><Minus size={13} /></GhostButton>
                    <GhostButton testId={`membership-inc-${m.id}`} onClick={() => adjust(m.id, 1)}><Plus size={13} /></GhostButton>
                  </div>
                  <DangerButton testId={`membership-cancel-${m.id}`} onClick={() => setCancelTarget(m.id)}>
                    <XCircle size={14} /> Cancel
                  </DangerButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel this membership?"
        message="The customer keeps access until the end of the current billing cycle, then the subscription stops renewing."
        confirmLabel="Cancel at Period End"
        accent="danger"
        busy={busy}
        onConfirm={confirmCancel}
        onClose={() => setCancelTarget(null)}
      />
    </div>
  );
};

export default MembershipsSection;
